/**
 * EVENT SERVICE
 * 
 * Firebase operations for the Events Calendar feature.
 * Location: src/services/eventService.ts
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import type { CalendarEvent, EventFormData, EventFilters } from '../types/event';

// ============================================
// CREATE EVENT
// ============================================

/**
 * Create a new event (brand owners only)
 */
export async function createEvent(
  formData: EventFormData,
  brandInfo: {
    brandId: string;
    brandName: string;
    brandSlug: string;
    brandLogo?: string;
  },
  userId: string
): Promise<string> {
  const eventsRef = collection(db, 'events');
  
  const eventData: Omit<CalendarEvent, 'id'> = {
    title: formData.title.trim(),
    description: formData.description.trim(),
    eventType: formData.eventType,
    
    startDate: Timestamp.fromDate(formData.startDate),
    endDate: formData.endDate ? Timestamp.fromDate(formData.endDate) : undefined,
    startTime: formData.startTime || undefined,
    endTime: formData.endTime || undefined,
    isAllDay: formData.isAllDay,
    timezone: formData.timezone || undefined,  // Store brand owner's timezone
    
    // Recurrence settings
    recurrence: formData.recurrence ? {
      pattern: formData.recurrence.pattern,
      weeklyDays: formData.recurrence.weeklyDays,
      monthlyDay: formData.recurrence.monthlyDay,
      monthlyWeek: formData.recurrence.monthlyWeek,
      monthlyWeekday: formData.recurrence.monthlyWeekday,
      endDate: formData.recurrence.endDate ? Timestamp.fromDate(formData.recurrence.endDate) : undefined
    } : undefined,
    
    brandId: brandInfo.brandId,
    brandName: brandInfo.brandName,
    brandSlug: brandInfo.brandSlug,
    brandLogo: brandInfo.brandLogo,
    
    shopUrl: formData.shopUrl?.trim() || undefined,
    socialUrl: formData.socialUrl?.trim() || undefined,
    
    featuredImage: formData.featuredImage || undefined,
    productsTeased: formData.productsTeased?.filter(p => p.trim()) || undefined,
    discountInfo: formData.discountInfo?.trim() || undefined,
    
    createdAt: serverTimestamp() as Timestamp,
    updatedAt: serverTimestamp() as Timestamp,
    createdBy: userId,
    
    isPublished: true,
    isFeatured: false
  };
  
  // Remove undefined values (Firestore doesn't like them)
  const cleanedData = Object.fromEntries(
    Object.entries(eventData).filter(([_, v]) => v !== undefined)
  );
  
  const docRef = await addDoc(eventsRef, cleanedData);
  return docRef.id;
}

// ============================================
// READ EVENTS
// ============================================

/**
 * Get a single event by ID
 */
export async function getEvent(eventId: string): Promise<CalendarEvent | null> {
  const eventRef = doc(db, 'events', eventId);
  const eventSnap = await getDoc(eventRef);
  
  if (!eventSnap.exists()) return null;
  
  return {
    id: eventSnap.id,
    ...eventSnap.data()
  } as CalendarEvent;
}

/**
 * Get upcoming events (for the main calendar view)
 */
export async function getUpcomingEvents(
  filters?: EventFilters,
  limitCount: number = 50
): Promise<CalendarEvent[]> {
  const eventsRef = collection(db, 'events');
  const now = Timestamp.fromDate(new Date());
  
  // Start with base query - upcoming published events
  let constraints: any[] = [
    where('isPublished', '==', true),
    where('startDate', '>=', now),
    orderBy('startDate', 'asc'),
    limit(limitCount)
  ];
  
  // Add type filter if specified
  if (filters?.eventTypes && filters.eventTypes.length > 0) {
    constraints = [
      where('isPublished', '==', true),
      where('eventType', 'in', filters.eventTypes),
      where('startDate', '>=', now),
      orderBy('startDate', 'asc'),
      limit(limitCount)
    ];
  }
  
  // Add brand filter if specified
  if (filters?.brandId) {
    constraints = [
      where('isPublished', '==', true),
      where('brandId', '==', filters.brandId),
      where('startDate', '>=', now),
      orderBy('startDate', 'asc'),
      limit(limitCount)
    ];
  }
  
  const q = query(eventsRef, ...constraints);
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as CalendarEvent[];
}

/**
 * Get events for a specific month (for calendar grid)
 */
export async function getEventsForMonth(
  year: number,
  month: number // 0-indexed (January = 0)
): Promise<CalendarEvent[]> {
  const eventsRef = collection(db, 'events');
  
  // Get first and last day of month
  const startOfMonth = new Date(year, month, 1);
  const endOfMonth = new Date(year, month + 1, 0, 23, 59, 59);
  
  const q = query(
    eventsRef,
    where('isPublished', '==', true),
    where('startDate', '>=', Timestamp.fromDate(startOfMonth)),
    where('startDate', '<=', Timestamp.fromDate(endOfMonth)),
    orderBy('startDate', 'asc')
  );
  
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as CalendarEvent[];
}

/**
 * Get events by a specific brand
 */
export async function getEventsByBrand(
  brandId: string,
  includeHistory: boolean = false
): Promise<CalendarEvent[]> {
  const eventsRef = collection(db, 'events');
  
  let q;
  if (includeHistory) {
    // Get all events, past and future
    q = query(
      eventsRef,
      where('brandId', '==', brandId),
      where('isPublished', '==', true),
      orderBy('startDate', 'desc')
    );
  } else {
    // Only upcoming events
    const now = Timestamp.fromDate(new Date());
    q = query(
      eventsRef,
      where('brandId', '==', brandId),
      where('isPublished', '==', true),
      where('startDate', '>=', now),
      orderBy('startDate', 'asc')
    );
  }
  
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as CalendarEvent[];
}

/**
 * Get events happening today or this week (for homepage widget)
 */
export async function getEventsThisWeek(): Promise<CalendarEvent[]> {
  const eventsRef = collection(db, 'events');
  
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfWeek = new Date(startOfToday.getTime() + 7 * 24 * 60 * 60 * 1000);
  
  const q = query(
    eventsRef,
    where('isPublished', '==', true),
    where('startDate', '>=', Timestamp.fromDate(startOfToday)),
    where('startDate', '<=', Timestamp.fromDate(endOfWeek)),
    orderBy('startDate', 'asc'),
    limit(10)
  );
  
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as CalendarEvent[];
}

// ============================================
// UPDATE EVENT
// ============================================

/**
 * Update an existing event
 */
export async function updateEvent(
  eventId: string,
  formData: EventFormData,
  brandInfo: {
    brandId: string;
    brandName: string;
    brandSlug: string;
    brandLogo?: string;
  }
): Promise<void> {
  const eventRef = doc(db, 'events', eventId);
  
  const updateData: Record<string, any> = {
    title: formData.title.trim(),
    description: formData.description.trim(),
    eventType: formData.eventType,
    
    startDate: Timestamp.fromDate(formData.startDate),
    endDate: formData.endDate ? Timestamp.fromDate(formData.endDate) : null,
    startTime: formData.startTime || null,
    endTime: formData.endTime || null,
    isAllDay: formData.isAllDay,
    timezone: formData.timezone || null,
    
    // Update brand info in case it changed
    brandId: brandInfo.brandId,
    brandName: brandInfo.brandName,
    brandSlug: brandInfo.brandSlug,
    brandLogo: brandInfo.brandLogo || null,
    
    shopUrl: formData.shopUrl?.trim() || null,
    socialUrl: formData.socialUrl?.trim() || null,
    
    featuredImage: formData.featuredImage || null,
    productsTeased: formData.productsTeased?.filter(p => p.trim()) || null,
    discountInfo: formData.discountInfo?.trim() || null,
    
    // Recurrence settings
    recurrence: formData.recurrence ? {
      pattern: formData.recurrence.pattern,
      weeklyDays: formData.recurrence.weeklyDays || null,
      monthlyDay: formData.recurrence.monthlyDay || null,
      monthlyWeek: formData.recurrence.monthlyWeek || null,
      monthlyWeekday: formData.recurrence.monthlyWeekday ?? null,
      endDate: formData.recurrence.endDate ? Timestamp.fromDate(formData.recurrence.endDate) : null
    } : null,
    
    updatedAt: serverTimestamp()
  };
  
  // Remove null values (Firestore prefers deleteField() but this is simpler)
  const cleanedData = Object.fromEntries(
    Object.entries(updateData).filter(([_, v]) => v !== null && v !== undefined)
  );
  
  await updateDoc(eventRef, cleanedData);
}

/**
 * Toggle event published status
 */
export async function toggleEventPublished(
  eventId: string,
  isPublished: boolean
): Promise<void> {
  const eventRef = doc(db, 'events', eventId);
  await updateDoc(eventRef, {
    isPublished,
    updatedAt: serverTimestamp()
  });
}

/**
 * Toggle event featured status (admin only)
 */
export async function toggleEventFeatured(
  eventId: string,
  isFeatured: boolean
): Promise<void> {
  const eventRef = doc(db, 'events', eventId);
  await updateDoc(eventRef, {
    isFeatured,
    updatedAt: serverTimestamp()
  });
}

// ============================================
// DELETE EVENT
// ============================================

/**
 * Delete an event
 */
export async function deleteEvent(eventId: string): Promise<void> {
  const eventRef = doc(db, 'events', eventId);
  await deleteDoc(eventRef);
}

// ============================================
// PERMISSION HELPERS
// ============================================

/**
 * Check if a user can manage events for a brand
 * (They must be the brand owner or an admin)
 */
export async function canUserManageEvents(
  userId: string,
  userEmail: string,
  brandId: string
): Promise<boolean> {
  // First check if user is admin
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);
  if (userSnap.exists() && userSnap.data()?.isAdmin) {
    return true;
  }
  
  // Then check if user owns this brand
  const brandRef = doc(db, 'brands', brandId);
  const brandSnap = await getDoc(brandRef);
  
  if (!brandSnap.exists()) return false;
  
  const brandData = brandSnap.data();
  
  // Check by UID
  if (brandData.claimedBy === userId) return true;
  
  // Check by email (for cases where user changed email)
  if (brandData.ownerEmail === userEmail) return true;
  
  return false;
}

/**
 * Get all brands a user can post events for
 */
export async function getUserBrands(
  userId: string,
  userEmail: string
): Promise<Array<{ id: string; name: string; slug: string; logo?: string; timezone?: string }>> {
  const brandsRef = collection(db, 'brands');
  
  // Query by claimedBy UID
  const byUidQuery = query(
    brandsRef,
    where('claimedBy', '==', userId),
    where('status', 'in', ['claimed', 'verified'])
  );
  
  const byUidSnapshot = await getDocs(byUidQuery);
  
  const brands = byUidSnapshot.docs.map(doc => ({
    id: doc.id,
    name: doc.data().name,
    slug: doc.data().slug,
    logo: doc.data().logo,
    timezone: doc.data().timezone
  }));
  
  // Also query by ownerEmail (in case UID doesn't match)
  const byEmailQuery = query(
    brandsRef,
    where('ownerEmail', '==', userEmail),
    where('status', 'in', ['claimed', 'verified'])
  );
  
  const byEmailSnapshot = await getDocs(byEmailQuery);
  
  // Add any brands found by email that aren't already in the list
  byEmailSnapshot.docs.forEach(doc => {
    if (!brands.some(b => b.id === doc.id)) {
      brands.push({
        id: doc.id,
        name: doc.data().name,
        slug: doc.data().slug,
        logo: doc.data().logo,
        timezone: doc.data().timezone
      });
    }
  });
  
  return brands;
}
